/**
 * The **venue time frame** — TMX's single answer to "what time is it at the
 * tournament?"
 *
 * ── Why this module exists ──
 *
 * A stored instant (`calledAt`, `scoredTime`, an embargo stamp) is a point on
 * the world's timeline. Turning it into a clock time or a calendar day requires
 * a zone, and TMX used to answer that question independently at ~a dozen sites,
 * every one of them with the **browser's** zone. A director running a Florida
 * event from a Pacific-set laptop read rest, call times and order-of-play timing
 * three hours out — silently, in numbers that stayed plausible, which is what
 * made it expensive. Two independent sightings on one day (TMX #1355, #1356)
 * made it a bug class rather than a bug.
 *
 * The decision is recorded in `Mentat/planning/DECISION_VENUE_TIME_FRAME.md`.
 * The short form:
 *
 *   **Every conversion of a stored instant to a clock time or a calendar day
 *   goes through this module, in the tournament's zone, resolved per instant.**
 *
 * ── Per instant, not a stored offset ──
 *
 * A bare `utcOffsetMinutes` is the offset at ONE moment. A tournament spanning a
 * DST change converts an hour wrong on the far side — silently, in figures
 * measured in minutes. Measured across the 2026 US spring-forward, 22:00 → 08:00
 * is nine hours, not ten. So every function here resolves the offset **at the
 * instant being converted**, against an IANA zone.
 *
 * That arithmetic is no longer TMX's. It lives in the factory as
 * `tools.zonedDateTime`, and this module calls it. TMX used to port it, because
 * the factory's copy was internal and unexported while the exported surface
 * (`tools.timeZone`) threw on bad input. The factory has since consolidated the
 * two and exported the result, so the port is deleted rather than maintained,
 * and there is now ONE implementation of wall-clock <-> instant in the ecosystem.
 *
 * What remains here is the part that is genuinely TMX's: which zone to read
 * against (`resolveVenueFrame`), what to do when the record names none, and the
 * shapes the UI wants.
 *
 * ── What is NOT an instant ──
 *
 * The distinction this module lives or dies by. Two different things in TMX look
 * alike and must never be run through the same conversion:
 *
 *   - **Instants** — `calledAt`, `scoredTime`, `updatedAt`, `createdAt`: full
 *     UTC ISO stamps. These need a zone. That is what this module is for.
 *   - **Naive wall clocks** — `scheduledTime`, `startTime`, `endTime`, court
 *     block `start`/`end`, and any `new Date('YYYY-MM-DDTHH:MM:SS')` with no
 *     trailing `Z`. These are ALREADY in venue wall-clock terms and carry no
 *     zone. Converting one is the bug, not the fix: read them with the raw
 *     `getHours()` accessors, which recover exactly the digits that were
 *     written. Comparisons between two naive values are zone-independent
 *     because the browser's zone cancels on both sides.
 *
 * Treating a naive wall clock as an instant — or the reverse — produces a figure
 * wrong by the UTC offset, which is worse than showing nothing.
 *
 * ── Which instants are venue-framed ──
 *
 * An instant describing something that happened **at the venue**, or that a
 * venue clock is measured against, is venue-framed: calls, scores, sign-ins,
 * embargoes, publish stamps. An instant describing the **operator's own
 * session** — a chat message, a local draft's `resolvedAt` — stays in the
 * operator's zone, because "when I saw it" is the question those answer. That
 * line is deliberate; it is not an oversight in the sites this module does not
 * touch.
 *
 * ── This file is TMX's Temporal migration seam ──
 *
 * The factory is moving to the TC39 Temporal API
 * (`Mentat/planning/FACTORY_TEMPORAL_MIGRATION.md`), and its Phase 1 —
 * `tools/timeZone.ts` on `Temporal.ZonedDateTime` — is this same problem solved
 * one repo over. TMX should follow, and the point of concentrating every
 * conversion here is that following is a rewrite of ONE file rather than a
 * sweep of the sixteen call sites this replaced.
 *
 * Three things were chosen with that migration in mind:
 *
 *   - **Signatures take and return strings, numbers and `Date`.** The factory's
 *     plan locks the wire format as ISO strings and changes only internal
 *     arithmetic, so nothing here has to change shape when the internals do.
 *     Callers never see a `Date` this module constructed except `venueNowOnDate`.
 *   - **Everything resolves per instant.** That is `ZonedDateTime`'s model
 *     already, so the migration is a simplification and not a redesign.
 *     `venueParts` becomes `instant.toZonedDateTimeISO(timeZone)` field reads;
 *     `venueOffsetMinutesAt` becomes `zdt.offsetNanoseconds`.
 *   - **The two-pass offset guess is no longer here to migrate.** It moved to
 *     the factory with the rest of the arithmetic, so when Temporal lands it is
 *     deleted once, in one repo, rather than in each copy. Whoever migrates
 *     should still read those two passes as a workaround to delete and not a
 *     behaviour to preserve: `Temporal.PlainDateTime.from(…).toZonedDateTime(tz)`
 *     does it in one step and turns the ambiguity they settle silently
 *     (spring-forward's missing hour, fall-back's repeated one) into an explicit
 *     `disambiguation` choice.
 *
 * The wart to fix when it lands: `venueNowOnDate` returns a **naive `Date`**
 * because that is the only shape native `Date` offers for "a wall clock with no
 * zone", and its callers compare it against other naive Dates. That is exactly
 * `Temporal.PlainDateTime`, typed — at which point the comparison stops being a
 * convention this file has to explain and becomes something the compiler knows.
 *
 * TMX is the ecosystem's iOS PWA and Safari has no native Temporal, so TMX will
 * need `@js-temporal/polyfill` imported at app boot before anything here runs.
 */
import { isValidTimeZone } from 'functions/getSupportedTimeZones';
import { tournamentEngine } from 'services/factory/engine';
import { tools } from 'tods-competition-factory';

const { offsetMinutesAt, zonedWallClockToMs } = tools.zonedDateTime;

export type VenueFrameSource = 'tournament' | 'browser';

export type VenueFrame = {
  /** IANA identifier, e.g. `America/New_York`. Always a usable zone. */
  timeZone: string;
  /**
   * `'tournament'` when the record carries a zone; `'browser'` when it does
   * not and the runtime's own zone is standing in. Callers that render times
   * to an operator should surface the `'browser'` case — see
   * `renderVenueFrameNotice`.
   */
  source: VenueFrameSource;
};

export type VenueParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const MS_PER_MINUTE = 60_000;

/** The runtime's own IANA zone, or `'UTC'` when the platform will not name one. */
export function browserTimeZone(): string {
  try {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof detected === 'string' && detected.length ? detected : 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * The frame every instant→clock conversion in TMX should be read against.
 *
 * `tournamentRecord.localTimeZone` is the authoritative venue zone: it is
 * operator-set through the Edit Dates modal, validated against a supported-zones
 * list, and never silently auto-applied (`getDetectedTimeZone()` is a nudge the
 * TD must confirm).
 *
 * When the record carries none, the browser's zone stands in — and the caller is
 * expected to say so on screen. Refusing to render times instead would break the
 * running desk for every zone-less tournament, which is most of them today; a
 * *silent* browser fallback would reproduce, through a different door, exactly
 * the wrongness this module exists to eliminate. So the fallback stays and
 * announces itself: the one case where the page can be wrong is the one case
 * where it says so.
 *
 * Cheap enough to call per site. What must never be re-implemented per site is
 * the **fallback rule** — that is the whole point of there being one function.
 */
export function resolveVenueFrame(): VenueFrame {
  let localTimeZone: string | undefined;
  try {
    localTimeZone = tournamentEngine.q.tournament()?.localTimeZone;
  } catch {
    localTimeZone = undefined;
  }
  if (isValidTimeZone(localTimeZone)) return { timeZone: localTimeZone, source: 'tournament' };
  return { timeZone: browserTimeZone(), source: 'browser' };
}

/** Convenience for the common case: the zone alone. */
export function venueTimeZone(): string {
  return resolveVenueFrame().timeZone;
}

function toDate(value?: string | number | Date): Date | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function resolveZone(timeZone?: string): string {
  return isValidTimeZone(timeZone) ? timeZone : browserTimeZone();
}

/**
 * An instant, broken into its calendar/clock fields **in the given zone**.
 *
 * Returns `undefined` for unparseable input rather than a substituted default —
 * a wrong time is worse than a missing one everywhere this is used.
 */
export function venueParts(value?: string | number | Date, timeZone?: string): VenueParts | undefined {
  const date = toDate(value);
  if (!date) return undefined;

  const ms = date.getTime();
  const offsetMinutes = offsetMinutesAt(ms, resolveZone(timeZone));
  if (offsetMinutes === undefined) return undefined;

  // Shift the instant by the zone's offset and read the fields back as UTC. The
  // factory's `zonedParts` does exactly this, but returns `YYYY-MM-DD` + `HH:MM`
  // strings; `venueNowOnDate` needs seconds, so the field read stays here while
  // the offset resolution — the part that was duplicated — does not.
  //
  // This also retires the `hour === '24'` normalisation the formatToParts read
  // needed: arithmetic on the instant cannot produce a 24th hour.
  const shifted = new Date(ms + offsetMinutes * MS_PER_MINUTE);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
  };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * ── "Now" is asked for by name ──
 *
 * The three converters below take a **required** instant. They used to default
 * a missing one to `new Date()`, and that shipped a production bug: an uncalled
 * matchUp has no `calledAt`, so `venueClock(matchUp.schedule.calledAt)`
 * rendered the *current* clock as its call time — identical on every row of a
 * tournament whose first day had not arrived, and advancing on each redraw
 * ([#1387(TMX)](https://github.com/CourtHive/TMX/pull/1387)). The formatter's own
 * `if (!clock) return ''` could not catch it, because "now" is a valid clock
 * string.
 *
 * The module already knew better and said so about `venueParts`:
 *
 * > *Returns `undefined` for unparseable input rather than a substituted
 * > default — a wrong time is worse than a missing one everywhere this is used.*
 *
 * Its own wrappers then substituted a default. That is `architectural-standards`
 * **A3 — open defaults are fail-open**, and `coding-standards`' *"a default is
 * not a detection"*, in the module those standards most need to hold.
 *
 * An `arguments.length` check would have been the cheap fix and the wrong one:
 * it breaks **forwarding wrappers**, which always pass one argument whether or
 * not their own caller did. `signInPresence.venueCalendarDay(value?)` was
 * exactly that, and would have started returning `''` for `officialsTab`'s bare
 * call — a silent regression rather than a compiler error.
 *
 * So the sites that genuinely mean "now" say so — `venueToday()`,
 * `venueNowClock()` — and everywhere else the compiler now requires an instant.
 * The next `venueClock(possiblyMissingStamp)` is a type error at the keyboard
 * instead of a plausible wrong time in production.
 */

/**
 * The calendar day an instant falls on **at the venue**, as `YYYY-MM-DD`.
 *
 * Never `toISOString().slice(0, 10)`: that reports the UTC day, so west of
 * Greenwich it rolls over while the tournament is still playing — at 8pm in
 * Florida it already says tomorrow. Shipping that once (#1352, fixed in #1355)
 * made every official read "available" every evening.
 *
 * Empty string when the instant is unreadable — and `''` is falsy, so a caller
 * that forgets to check gets a missing day rather than a wrong one.
 */
export function venueCalendarDate(value: string | number | Date, timeZone?: string): string {
  const parts = venueParts(value, timeZone);
  if (!parts) return '';
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

/** An instant as a `HH:MM` venue wall clock. Empty string when unparseable. */
export function venueClock(value: string | number | Date, timeZone?: string): string {
  const parts = venueParts(value, timeZone);
  if (!parts) return '';
  return `${pad2(parts.hour)}:${pad2(parts.minute)}`;
}

/** An instant as minutes from venue midnight (`14:20` → `860`). */
export function venueDayMinutes(value: string | number | Date, timeZone?: string): number | undefined {
  const parts = venueParts(value, timeZone);
  if (!parts) return undefined;
  return parts.hour * 60 + parts.minute;
}

/**
 * Today's calendar day **at the venue**, `YYYY-MM-DD`.
 *
 * The "what day is it?" every schedule surface asks. Named rather than reached
 * by omitting an argument, so that asking for today and forgetting to pass a
 * stamp stop looking identical in the source.
 */
export function venueToday(timeZone?: string): string {
  return venueCalendarDate(new Date(), timeZone);
}

/** The current venue wall clock, `HH:MM`. The named form of "now" — see `venueToday`. */
export function venueNowClock(timeZone?: string): string {
  return venueClock(new Date(), timeZone);
}

/**
 * The zone's UTC offset **at a particular instant**, in minutes east of UTC.
 *
 * The whole reason this takes an instant rather than returning a constant: a
 * zone's offset is a function of when you ask.
 */
export function venueOffsetMinutesAt(value: string | number | Date, timeZone?: string): number | undefined {
  const date = toDate(value);
  if (!date) return undefined;
  return offsetMinutesAt(date.getTime(), resolveZone(timeZone));
}

/**
 * The inverse: a venue wall clock (`'2026-08-25'`, `'14:20'`) → absolute UTC ms.
 *
 * Needed wherever a planned time has to be compared with an actual instant —
 * the Call Timing Variance report is the case that forced it, where "scheduled
 * 15:00" and "called at <ISO>" must be subtracted.
 *
 * Two passes, because resolving the offset needs an instant and finding the
 * instant needs the offset: guess with the offset at the naive-as-UTC moment,
 * then re-resolve at the guess. That converges everywhere except the one hour
 * that does not exist (spring-forward) and the one that happens twice
 * (fall-back), where it settles on a consistent answer rather than a correct
 * one — there isn't a correct one to settle on.
 */
export function venueWallClockToMs(date?: string, clock?: string, timeZone?: string): number | undefined {
  // `resolveZone` guarantees a zone the factory will recognise, so the refusal
  // path below can only mean a malformed date or clock.
  return zonedWallClockToMs({ date: date?.trim(), time: clock?.trim(), timeZone: resolveZone(timeZone) })?.ms;
}

/**
 * "Now", projected onto the venue wall clock of `stripDate`, as a **naive**
 * Date — i.e. one whose raw `getHours()` accessors read back the venue clock.
 * `undefined` when `stripDate` is still ahead of the venue's today, because
 * there is no such instant.
 *
 * That naivety is deliberate and load-bearing. The values this gets compared
 * against (court block `start`/`end`) are themselves naive `YYYY-MM-DDTHH:MM:SS`
 * strings in venue wall-clock terms, parsed as browser-local. Comparing two
 * naive Dates cancels the browser's zone on both sides; the only thing that has
 * to be right is the **time-of-day**, and that is what this fixes.
 *
 * Projecting today's time-of-day onto another date is what lets the live-strip
 * warnings work when the operator is viewing a non-today date — which is not an
 * exotic case: the schedule opens on a tournament's last date once its dates are
 * past, so anyone running a past-dated tournament in real time is in it
 * permanently.
 *
 * **That projection only runs backwards.** Onto a future day it does not
 * describe a clock, it invents one: at 22:51 the evening before, every court
 * block painted on tomorrow before 22:51 reads as in effect *right now*, and a
 * match dropped onto tomorrow's Now strip is warned about a block that has not
 * happened. The same assumption, one module over, badged a player "on court" in
 * a tournament with no courts — see `nowDayMinutes()` in
 * `pages/tournament/tabs/scheduleViews/inspectorRest.ts`, which takes the
 * matching position for rest.
 *
 * So a future date returns `undefined` rather than a fabricated Date. Callers
 * must decide what "there is no now on that day" means for them; they cannot
 * accidentally compare against a time nobody has reached yet. A malformed
 * `stripDate` takes the same exit — it sorts after any real date, and an
 * Invalid Date was never a useful answer either.
 */
export function venueNowOnDate(stripDate: string, timeZone?: string): Date | undefined {
  const parts = venueParts(new Date(), timeZone);
  if (!parts) return new Date(`${stripDate}T00:00:00`);
  // `YYYY-MM-DD` sorts lexicographically, so no parsing is needed to order two
  // of them — and the comparison is made on the venue's today, never the
  // browser's, which is the whole point of this module.
  const todayAtVenue = `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
  if (stripDate.slice(0, 10) > todayAtVenue) return undefined;
  return new Date(`${stripDate}T${pad2(parts.hour)}:${pad2(parts.minute)}:${pad2(parts.second)}`);
}
